import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home, FireExtinguisher } from '@home-guard/shared';

const CURRENT_YEAR = new Date().getFullYear();

const SUGGESTED_LOCATIONS = [
  'Kitchen', 'Garage', 'Basement', 'Master bedroom', 'Hallway — upstairs',
  'Hallway — downstairs', 'Near fireplace', 'Mechanical room', 'Workshop',
];

const schema = z.object({
  location:       z.string().min(1, 'Location is required').max(100).trim(),
  type:           z.enum(['ABC','BC','K','CO2','']).optional(),
  size_lbs:       z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().min(0.5).max(500).nullable()),
  last_inspected: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal('')),
  last_recharged: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal('')),
  purchase_year:  z.preprocess(v => (v === '' || v == null ? null : Number(v)),
    z.number().int().min(1950).max(CURRENT_YEAR + 1).nullable()),
  notes:          z.string().max(500).trim().optional(),
});
type FormData = z.infer<typeof schema>;

export function AddFireExtinguisher() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const home = (homesData?.data ?? []).find(h => h.is_primary) ?? homesData?.data?.[0];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const location = watch('location');

  const mutation = useMutation({
    mutationFn: (data: FormData) => api.post<FireExtinguisher>(`/homes/${home!.id}/fire-extinguishers`, {
      location:       data.location,
      type:           data.type || null,
      size_lbs:       data.size_lbs,
      last_inspected: data.last_inspected || null,
      last_recharged: data.last_recharged || null,
      purchase_year:  data.purchase_year,
      notes:          data.notes || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fire-extinguishers', home?.id] });
      navigate('/app/fire-extinguishers');
    },
  });

  if (!home) return null;

  const onSubmit = (data: FormData) => mutation.mutate(data);

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 100 }}>
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 16 }}
        >
          ← Back
        </button>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#fff' }}>Add Fire Extinguisher</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Location */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
          <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
            Location *
          </label>
          <input
            {...register('location')}
            placeholder="e.g. Kitchen"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: errors.location ? '1.5px solid #ef4444' : '1.5px solid #d1d5db',
              fontSize: 15, boxSizing: 'border-box',
            }}
          />
          {errors.location && <p style={{ margin: '4px 0 0', color: '#ef4444', fontSize: 12 }}>{errors.location.message}</p>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {SUGGESTED_LOCATIONS.map(loc => (
              <button
                key={loc}
                type="button"
                onClick={() => setValue('location', loc)}
                style={{
                  padding: '5px 10px', borderRadius: 6, fontSize: 12,
                  border: location === loc ? '1.5px solid #1D4ED8' : '1.5px solid #d1d5db',
                  background: location === loc ? '#eff6ff' : '#f9fafb',
                  color: location === loc ? '#1D4ED8' : '#6b7280',
                  cursor: 'pointer',
                }}
              >
                {loc}
              </button>
            ))}
          </div>
        </div>

        {/* Type & size */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 8 }}>
              Extinguisher type
            </label>
            <select
              {...register('type')}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15 }}
            >
              <option value="">Select type…</option>
              <option value="ABC">ABC dry chemical (recommended for home)</option>
              <option value="BC">BC (grease & flammable liquids)</option>
              <option value="K">Class K (professional kitchen)</option>
              <option value="CO2">CO₂</option>
            </select>
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
              Size (lbs)
            </label>
            <input
              {...register('size_lbs')}
              type="number"
              min={0.5}
              max={500}
              step={0.5}
              placeholder="e.g. 5"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Purchase year & dates */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
              Purchase year
            </label>
            <input
              {...register('purchase_year')}
              type="number"
              min={1950}
              max={CURRENT_YEAR}
              placeholder={String(CURRENT_YEAR)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
              Last visually inspected
            </label>
            <input
              {...register('last_inspected')}
              type="date"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 15, color: '#111827', display: 'block', marginBottom: 4 }}>
              Last recharged / hydrotested
            </label>
            <input
              {...register('last_recharged')}
              type="date"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <div style={{
          padding: '12px 14px', background: '#fffbeb', borderRadius: 10,
          border: '1px solid #fde68a', fontSize: 13, color: '#92400e',
        }}>
          A 5 lb ABC dry chemical extinguisher is recommended for each kitchen, garage, and floor of your home. Replace or hydrotest after 12 years per NFPA 10.
        </div>

        {mutation.error instanceof Error && (
          <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#ef4444', fontSize: 14 }}>
            Failed to add extinguisher. Please try again.
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          style={{
            padding: '14px', background: '#1D4ED8', color: '#fff', border: 'none',
            borderRadius: 12, fontWeight: 600, fontSize: 16, cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting || mutation.isPending ? 'Adding…' : 'Add extinguisher'}
        </button>
      </form>

      <NavBar />
    </div>
  );
}
